import { WidgetTarget } from "../../types/dashboard.types";
import { DashboardTenant } from "../../utils/dashboard/moduleTenants";
import { ALL_TENANTS_LABEL } from "./dashboardConfig";
import type { WidgetSelectOption } from "./WidgetSelect";

const ALL_TENANTS = "__all__";

/** What every tenant-scoped widget receives from the page. */
export interface TenantWidgetProps {
	/** Tenants where the widget's module is available to the user. */
	tenants: DashboardTenant[];
	currentTenantKey: string;
}

/** Names the master tenant's share in the all-tenants view. */
export const masterTenantName = (tenants: DashboardTenant[]): string =>
	tenants.find((tenant) => tenant.isMaster)?.name ?? "Master tenant";

/**
 * What "All tenants" means for a widget:
 * - "master": every tenant's records, read on the master tenant's URL;
 * - "mine": the caller's own records in every tenant (My tasks);
 * - "none": not offered.
 */
export type AllTenantsMode = "master" | "mine" | "none";

export interface TenantChoice {
	options: WidgetSelectOption<string>[];
	defaultValue: string;
	/** The request target and display name for a selected value. */
	resolve: (value: string) => { target: WidgetTarget | null; label: string };
}

/** The tenant whose URL serves the "All tenants" view, if it is offered. */
const allTenantsUrlKeyFor = (
	tenants: DashboardTenant[],
	mode: AllTenantsMode,
	currentTenantKey: string
): string | undefined => {
	if (mode === "mine") return currentTenantKey;
	if (mode === "master") return tenants.find((tenant) => tenant.isMaster)?.key;
	return undefined;
};

export const tenantChoice = (
	tenants: DashboardTenant[],
	mode: AllTenantsMode,
	currentTenantKey: string
): TenantChoice => {
	const allTenantsUrlKey = allTenantsUrlKeyFor(tenants, mode, currentTenantKey);
	const homeTenant =
		tenants.find((tenant) => tenant.key === currentTenantKey) ?? tenants[0];
	const defaultValue = allTenantsUrlKey ? ALL_TENANTS : (homeTenant?.key ?? "");

	const options: WidgetSelectOption<string>[] = [
		...(allTenantsUrlKey
			? [
					{
						value: ALL_TENANTS,
						label: ALL_TENANTS_LABEL,
						note: mode === "master" ? "every tenant" : undefined,
					},
				]
			: []),
		...tenants.map((tenant) => ({
			value: tenant.key,
			label: tenant.name,
			note: mode === "master" && tenant.isMaster ? "own only" : undefined,
		})),
	];

	const resolve = (value: string) => {
		const isOffered = options.some((option) => option.value === value);
		const selected = isOffered ? value : defaultValue;
		if (selected === ALL_TENANTS && allTenantsUrlKey) {
			return {
				target: { tenantKey: allTenantsUrlKey, scope: "all" as const },
				label: ALL_TENANTS_LABEL,
			};
		}
		const tenant = tenants.find((item) => item.key === selected);
		return tenant
			? {
					target: { tenantKey: tenant.key, scope: "tenant" as const },
					label: tenant.name,
				}
			: { target: null, label: "" };
	};

	return { options, defaultValue, resolve };
};
